import { useTheme } from '@emotion/react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    completeNewPasswordChallenge,
    resetPassword,
} from '../../../services/CognitoAuthService';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Collapse,
    CssBaseline,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material';
import Logos from '../../../images/logos.png';
import BGImage from '../../../images/Login BG image.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';

function Copyright(props) {
    return (
        <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            {...props}
        >
            {'Copyright © '}
            <Link href="https://effectivemk.com/" style={{ color: '#0B3E83' }}>
                Effective Marketing
            </Link>
            {' & '}
            <Link href="https://www.winnermk.com/" style={{ color: '#0B3E83' }}>
                Winner Marketing
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const passwordRules = [
    { label: 'Mínimo 8 caracteres', test: (pwd) => pwd?.length >= 8 },
    { label: 'Al menos una letra mayúscula', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'Al menos una letra minúscula', test: (pwd) => /[a-z]/.test(pwd) },
    { label: 'Al menos un número', test: (pwd) => /[0-9]/.test(pwd) },
    {
        label: 'Al menos un carácter especial',
        test: (pwd) => /[^A-Za-z0-9]/.test(pwd),
    },
];

const PasswordChallenge = () => {
    const [alert, setAlert] = useState({
        show: false,
        title: '',
        message: '',
        type: '',
    });
    const [validate, setValidation] = useState({
        show: false,
        helperText: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [showPasswords, setShowPasswords] = useState({
        new: false,
        confirm: false,
    });

    const navigate = useNavigate();
    const defaultTheme = useTheme();
    const location = useLocation();

    const imgUrl = location?.state?.imgUrl;
    const isNewPassword = location?.state?.isNewPassword;

    const onResetPassword = async (event) => {
        try {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            if (isNewPassword) {
                await completeNewPasswordChallenge(
                    data.get('confirm_password'),
                );
            } else {
                await resetPassword(
                    data.get('code'),
                    data.get('confirm_password'),
                    location?.state?.email,
                );
            }

            setAlert({
                show: true,
                message: 'Contraseña cambiada correctamente',
            });
            setTimeout(() => {
                setAlert({
                    show: false,
                    title: '',
                    message: '',
                });
                navigate('/auth/login');
            }, 2000);
        } catch (error) {
            setAlert({
                show: true,
                message: handleOnResetPasswordError(error),
                type: 'error',
            });
            setTimeout(() => {
                setAlert({ show: false, title: '', message: '' });
            }, 5000);
        }
    };

    const validatePasswords = (newPassword, confirmPassword) => {
        if (newPassword !== confirmPassword) {
            setValidation({
                show: true,
                helperText: 'Las contraseñas no coinciden',
                newPassword,
                confirmPassword,
            });
        } else {
            setValidation({
                ...validate,
                show: false,
                helperText: '',
                newPassword,
                confirmPassword,
            });
        }
    };

    const handleOnResetPasswordError = (error) => {
        if (error.name === 'InvalidPasswordException') {
            if (error.message.includes('^\\S.*\\S$')) {
                return 'La contraseña no tiene al menos 8 caracteres, verifica e intenta nuevamente';
            }
            if (error.message.includes('uppercase')) {
                return 'La contraseña no tiene al menos 1 mayúscula';
            }
            if (error.message.includes('numeric')) {
                return 'La contraseña no tiene al menos 1 carácter numérico';
            }
            if (error.message.includes('symbol')) {
                return 'La contraseña no tiene al menos 1 símbolo';
            }
        }
        return error.message;
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <Grid
                container
                component="main"
                sx={{
                    minHeight: '100vh',
                    backgroundImage: `url('${BGImage}')`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                }}
            >
                <Grid
                    container
                    component={Paper}
                    elevation={5}
                    square
                    sx={{
                        width: '90vw',
                        maxWidth: '1200px',
                        margin: '5vh auto',
                    }}
                >
                    <CssBaseline />
                    <Grid item xs={12} sm={8} md={6}>
                        <Box
                            sx={{
                                my: 8,
                                mx: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Typography component="h1" variant="h5">
                                Establecer Nueva Contraseña
                            </Typography>
                            <Box
                                component="img"
                                sx={{
                                    height: '10vh',
                                    maxHeight: { xs: '10vh', md: '15vh' },
                                }}
                                alt="logos"
                                src={Logos}
                            ></Box>
                            <Box
                                sx={{
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginY: 2,
                                    width: '100%',
                                    textAlign: 'left', // Alineación del texto a la izquierda
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 'bold',
                                        marginBottom: '8px',
                                        textAlign: 'center', // Título centrado
                                    }}
                                >
                                    La contraseña debe contener:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                    {passwordRules.map((rule, index) => (
                                        <Box
                                            component="li"
                                            key={index}
                                            sx={{
                                                color: rule.test(
                                                    validate.newPassword,
                                                )
                                                    ? 'green'
                                                    : 'red',
                                                fontSize: '0.9rem',
                                                marginBottom: '4px',
                                                textAlign: 'left', // También asegura que los ítems estén alineados
                                            }}
                                        >
                                            {rule.label}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box
                                component="form"
                                noValidate
                                onSubmit={onResetPassword}
                                sx={{ mt: 1 }}
                            >
                                {!isNewPassword && (
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="code"
                                        label="Código de verificación"
                                        name="code"
                                        autoComplete="code"
                                        autoFocus
                                    />
                                )}
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="newPassword"
                                    label="Nueva contraseña"
                                    name="newPassword"
                                    autoComplete="current-password"
                                    autoFocus
                                    type={
                                        showPasswords.new ? 'text' : 'password'
                                    }
                                    onChange={(event) =>
                                        validatePasswords(
                                            event.target.value,
                                            validate.confirmPassword,
                                        )
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPasswords({
                                                            ...showPasswords,
                                                            new: !showPasswords.new,
                                                        })
                                                    }
                                                    edge="end"
                                                    aria-label="toggle password visibility"
                                                >
                                                    {showPasswords.new ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    error={validate.show}
                                    helperText={validate.helperText}
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="confirm_password"
                                    label="Confirmar Contraseña"
                                    type={
                                        showPasswords.confirm
                                            ? 'text'
                                            : 'password'
                                    }
                                    id="confirm_password"
                                    autoComplete="current-password"
                                    onChange={(event) =>
                                        validatePasswords(
                                            validate.newPassword,
                                            event.target.value,
                                        )
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPasswords({
                                                            ...showPasswords,
                                                            confirm:
                                                                !showPasswords.confirm,
                                                        })
                                                    }
                                                    edge="end"
                                                    aria-label="toggle confirm password visibility"
                                                >
                                                    {showPasswords.confirm ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {alert.show && (
                                    <Collapse in={alert.show}>
                                        <Alert severity={alert.type}>
                                            <AlertTitle
                                                sx={{ textAlign: 'left' }}
                                            >
                                                {alert.title}
                                            </AlertTitle>
                                            {alert.message}
                                        </Alert>
                                    </Collapse>
                                )}
                                <Button
                                    disabled={validate.show}
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                >
                                    Actualizar Contraseña
                                </Button>
                                <Copyright sx={{ mt: 5 }} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={false}
                        sm={4}
                        md={6}
                        sx={{
                            backgroundImage: `url(${imgUrl})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: (t) =>
                                t.palette.mode === 'light'
                                    ? t.palette.grey[50]
                                    : t.palette.grey[900],
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                </Grid>
            </Grid>
        </ThemeProvider>
    );
};

export default PasswordChallenge;
